import { forwardRef, useMemo, useState } from 'react';
import { Platform, Pressable, TextInput, TextInputProps, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Caption, ThemedText } from './Text';

export type InputFieldProps = TextInputProps & {
  label?: string;
  helperText?: string;
  errorText?: string;
  required?: boolean;
  showPasswordToggle?: boolean;
  /** Web form autofill / semantics only; not a React Native TextInput prop on native. */
  name?: string;
};

export const InputField = forwardRef<TextInput, InputFieldProps>(
  ({ label, helperText, errorText, required, style, name, showPasswordToggle = false, secureTextEntry, ...rest }, ref) => {
    const theme = useTheme();
    const [focused, setFocused] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const shouldShowPasswordToggle = showPasswordToggle && !!secureTextEntry;
    const effectiveSecureTextEntry = shouldShowPasswordToggle ? !passwordVisible : secureTextEntry;

    const borderColor = errorText
      ? theme.colors.error
      : focused
        ? theme.colors.primary
        : theme.colors.border;

    const labelColor = errorText
      ? theme.colors.error
      : focused
        ? theme.colors.primary
        : theme.colors.textSecondary;

    // Build accessibility label with required indicator
    const baseLabel = label || rest.placeholder || 'Text input';
    const accessibilityLabel = rest.accessibilityLabel || 
      (required ? `${baseLabel}, required` : baseLabel);
    
    // Build accessibility hint with error or helper text
    const accessibilityHint = rest.accessibilityHint || 
      (errorText ? `Error: ${errorText}` : helperText || (required ? 'Required field' : undefined));

    // Generate id/name from label for form autofill support
    const generateId = (text?: string): string | undefined => {
      if (!text) return undefined;
      return text
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || undefined;
    };
    
    const fieldId = useMemo(
      () => rest.id || generateId(label) || generateId(rest.placeholder) || 'input-field',
      [label, rest.id, rest.placeholder],
    );
    const fieldName = name || fieldId;

    return (
      <View style={{ gap: theme.spacing(0.75) }}>
        {label && (
          <ThemedText variant="bodyStrong" color={labelColor}>
            {label}
            {required && <ThemedText color={theme.colors.error}> *</ThemedText>}
          </ThemedText>
        )}
        <View style={{ position: 'relative' }}>
          <TextInput
            ref={ref}
            {...(Platform.OS === 'web' ? { id: fieldId, name: fieldName } : {})}
            placeholderTextColor={theme.colors.textSecondary}
            accessibilityLabel={accessibilityLabel}
            accessibilityHint={accessibilityHint}
            accessibilityState={{
              disabled: rest.editable === false,
            }}
            accessibilityRequired={required}
            secureTextEntry={effectiveSecureTextEntry}
            style={[
              {
                borderWidth: 1,
                borderColor,
                borderRadius: theme.radii.md,
                paddingHorizontal: theme.spacing(2),
                paddingVertical: theme.spacing(1.5),
                backgroundColor: theme.colors.surface,
                color: theme.colors.text,
                fontSize: 16,
                minHeight: 44, // Ensure minimum touch target
              },
              shouldShowPasswordToggle ? { paddingRight: theme.spacing(6) } : null,
              style,
            ]}
            onFocus={(event) => {
              setFocused(true);
              rest.onFocus?.(event);
            }}
            onBlur={(event) => {
              setFocused(false);
              rest.onBlur?.(event);
            }}
            {...rest}
          />
          {shouldShowPasswordToggle ? (
            <Pressable
              onPress={() => setPasswordVisible((visible) => !visible)}
              accessibilityRole="button"
              accessibilityLabel={passwordVisible ? 'Hide password' : 'Show password'}
              accessibilityState={{ selected: passwordVisible }}
              hitSlop={8}
              style={({ pressed }) => ({
                position: 'absolute',
                right: theme.spacing(1),
                top: 0,
                bottom: 0,
                width: 44,
                alignItems: 'center',
                justifyContent: 'center',
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <Ionicons
                name={passwordVisible ? 'eye-off-outline' : 'eye-outline'}
                size={20}
                color={theme.colors.textSecondary}
              />
            </Pressable>
          ) : null}
        </View>
        {(errorText || helperText) && (
          <Caption 
            color={errorText ? theme.colors.error : theme.colors.textSecondary}
            accessibilityLiveRegion={errorText ? "polite" : undefined}
            accessibilityRole={errorText ? "alert" : undefined}
          >
            {errorText || helperText}
          </Caption>
        )}
      </View>
    );
  },
);

InputField.displayName = 'InputField';


